import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { OfflineProvider } from './contexts/OfflineContext'
import { CategoryProvider } from './contexts/CategoryContext'
import Layout from './components/Layout/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Dashboard from './pages/Dashboard'
import POS from './pages/POS'
import KDS from './pages/KDS'
import Inventory from './pages/Inventory'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import MenuManagement from './pages/MenuManagement'
import CategoryManagement from './pages/CategoryManagement'
import TableManagement from './pages/TableManagement'
import WaiterModule from './pages/WaiterModule'
import Orders from './pages/Orders'
import Reservations from './pages/Reservations'
import Customers from './pages/Customers'
import Accounting from './pages/Accounting'
import HRManagement from './pages/HRManagement'
import Invoices from './pages/Invoices'
import Payments from './pages/Payments'
import Login from './pages/Login'
import LoadingScreen from './components/UI/LoadingScreen'
import { apiService, resolveImageUrl } from './services/api'

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [loadingIconUrl, setLoadingIconUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const minDelay = new Promise<void>(r => setTimeout(r, 1500))

    async function init() {
      try {
        const res = await apiService.getRestaurantSettings()
        if (cancelled) return
        if (res.success && res.data) {
          const data = res.data as { app_favicon_url?: string; app_loading_icon_url?: string }
          if (data.app_favicon_url) {
            const fullUrl = resolveImageUrl(data.app_favicon_url)
            let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
            if (!link) {
              link = document.createElement('link')
              link.rel = 'icon'
              document.head.appendChild(link)
            }
            link.href = fullUrl
          }
          if (data.app_loading_icon_url) {
            setLoadingIconUrl(resolveImageUrl(data.app_loading_icon_url))
          }
        }
      } catch {
        // ignore
      }
      await minDelay
      if (!cancelled) setIsLoading(false)
    }

    init()
    return () => { cancelled = true }
  }, [])

  if (isLoading) {
    return <LoadingScreen loadingIconUrl={loadingIconUrl ?? undefined} />
  }

  return (
      <ThemeProvider>
        <AuthProvider>
          <OfflineProvider>
            <CategoryProvider>
              <Router>
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
              <Routes>
                <Route path="/login" element={<Login />} />
                    <Route path="/" element={<Layout />}>
                      <Route index element={<ProtectedRoute requiredPermission="reports.sales.read"><Dashboard /></ProtectedRoute>} />
                      <Route path="pos" element={<ProtectedRoute requiredPermission="pos.orders.create"><POS /></ProtectedRoute>} />
                      <Route path="orders" element={<ProtectedRoute requiredPermission="pos.orders.read"><Orders /></ProtectedRoute>} />
                      <Route path="kds" element={<ProtectedRoute requiredPermission="kds.view"><KDS /></ProtectedRoute>} />
                      <Route path="menu" element={<ProtectedRoute requiredPermission="menu.items.read"><MenuManagement /></ProtectedRoute>} />
                      <Route path="categories" element={<ProtectedRoute requiredPermission="menu.categories.read"><CategoryManagement /></ProtectedRoute>} />
                      <Route path="tables" element={<ProtectedRoute requiredPermission="tables.read"><TableManagement /></ProtectedRoute>} />
                      <Route path="reservations" element={<ProtectedRoute requiredPermission="tables.read"><Reservations /></ProtectedRoute>} />
                      <Route path="waiter" element={<ProtectedRoute requiredPermission="waiter.orders.create"><WaiterModule /></ProtectedRoute>} />
                      <Route path="inventory" element={<ProtectedRoute requiredPermission="inventory.read"><Inventory /></ProtectedRoute>} />
                    <Route path="reports" element={<ProtectedRoute requiredPermission="reports.sales.read"><Reports /></ProtectedRoute>} />
                    <Route path="customers" element={<ProtectedRoute requiredPermission="customers.read"><Customers /></ProtectedRoute>} />
                    <Route path="accounting" element={<ProtectedRoute requiredPermission="accounting.accounts.read"><Accounting /></ProtectedRoute>} />
                    <Route path="invoices" element={<ProtectedRoute requiredPermission="accounting.invoices.manage"><Invoices /></ProtectedRoute>} />
                    <Route path="payments" element={<ProtectedRoute requiredPermission="pos.orders.read"><Payments /></ProtectedRoute>} />
                    <Route path="hr" element={<ProtectedRoute requiredPermission="hr.employees.read"><HRManagement /></ProtectedRoute>} />
                    <Route path="settings" element={<ProtectedRoute requiredPermission="settings.restaurant.read"><Settings /></ProtectedRoute>} />
                    </Route>
              </Routes>
            </div>
              </Router>
            </CategoryProvider>
          </OfflineProvider>
        </AuthProvider>
      </ThemeProvider>
  )
}

export default App
